"use client"

import type { ReactNode } from "react"
import { motion, useReducedMotion } from "motion/react"

import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"

export const ANALYSIS_RESULT_FRAME =
  "bg-white border border-green-100/70 rounded-3xl shadow-[0_1px_2px_rgba(16,24,16,0.04),0_24px_64px_-32px_rgba(22,101,52,0.16)]"

export const ANALYSIS_SECTION_CARD =
  "rounded-2xl border border-gray-100 bg-white shadow-[0_1px_2px_rgba(16,24,16,0.04),0_12px_30px_-24px_rgba(22,101,52,0.18)]"

export function AnalysisResultFrame({
  children,
  className,
  cardClassName,
  contentClassName,
}: {
  children: ReactNode
  className?: string
  cardClassName?: string
  contentClassName?: string
}) {
  const reduce = useReducedMotion()
  return (
    <motion.div
      className={cn("mx-auto max-w-6xl", className)}
      initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={
        reduce
          ? { opacity: { duration: 0.3, ease: "easeOut" } }
          : {
              y: { type: "spring", stiffness: 240, damping: 24, mass: 0.8 },
              opacity: { duration: 0.4, ease: "easeOut" },
            }
      }
    >
      <Card className={cn(ANALYSIS_RESULT_FRAME, cardClassName)}>
        <CardContent className={cn("p-3 md:p-10 lg:p-12", contentClassName)}>
          {children}
        </CardContent>
      </Card>
    </motion.div>
  )
}

export function AnalysisVerdictHeader({
  eyebrow,
  title,
  description,
  chips,
  icon,
  tone = "confirmed",
  className,
}: {
  eyebrow: string
  title: ReactNode
  description?: ReactNode
  chips?: ReactNode
  icon?: ReactNode
  tone?: "confirmed" | "uncertain" | "error"
  className?: string
}) {
  const tones = {
    confirmed: "border-green-100 bg-green-50 text-green-700",
    uncertain: "border-amber-200 bg-amber-50 text-amber-800",
    error: "border-red-200 bg-red-50 text-red-700",
  }

  return (
    <header className={cn("mb-5 text-center md:mb-8", className)}>
      <div className={cn("mb-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] md:mb-4", tones[tone])}>
        {icon}
        {eyebrow}
      </div>
      <h1 className="text-[1.75rem] font-extrabold leading-[1.08] tracking-[-0.02em] text-gray-900 md:text-4xl lg:text-[2.75rem] lg:leading-[1.05]">
        {title}
      </h1>
      {description ? <div className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-gray-600 md:text-base">{description}</div> : null}
      {chips ? <div className="mt-3 flex flex-wrap items-center justify-center gap-2 md:mt-4">{chips}</div> : null}
    </header>
  )
}
